// Oud for the oud style (docs/AUDIO_DESIGN.md §12): the fretless, short-necked Arabic lute, plucked with a plectrum (the
// risha) over a deep bowl. Every pluck is a course of two strings in near-unison rendered as Karplus–Strong strings in JS
// into an AudioBuffer (cached per page) and played by one AudioBufferSource + one gain per note. The oud slides into notes
// (no frets), plays long notes as a tremolo (rish), opens held chords with a little run in the maqam, and its low courses,
// doubled by a soft sub, are the style's bass.
import { rng, clamp } from '../../engine/core.js';
import { mtof, hold } from '../dsp.js';
import { gain, filter, osc, panner, persistent } from '../instruments.js';

const mod12 = n => ((n % 12) + 12) % 12;
/** a seed from an event's numbers: the instruments draw each event's choices (tremolo or not, a run, a slide, the
 *  humanising) from it, so every radio listener hears the same notes whether they tuned in at the top or half-way */
export const seedOf = (...xs) => { let h = 2166136261; for (const x of xs) h = Math.imul(h ^ Math.round(x * 1e6), 16777619); return h >>> 0; };

/* ---------------- plucks: two Karplus–Strong strings per course, at half the context rate ---------------- */
// One string: y[n] = e[n] for the first lap (n < L), then T(H(y[n − L]))   e = the pluck, H = the loss one-pole (meets the
// T60 at f0 and at 1.8 kHz), T = the tuning allpass (no dispersion: gut and nylon are not stiff). The course sums string A
// at −c/2 ¢ and string B at +c/2 ¢ (c = 1.5–3.5 ¢ per note: the oud's courses are tuned closer than a lute's), B .8 as
// loud and 0.2–0.6 ms later (one plectrum stroke catches both).
const CACHE = new Map();                              // `${rate}:${midi}` → AudioBuffer, built once, lazily
export const OUD_STATS = { built: 0, ms: 0, maxMs: 0 };
const FB = 1800, TWO_PI = Math.PI * 2;
//   pick position β · pick LP (Hz) · noise share · T60 at 1.8 kHz (s): the plectrum strikes a fifth of the way along, over
//   the rosettes; the strings lose their highs fast (a woody "thunk" rather than the lute's ring)
const PLEC = { beta: 0.19, lp: 3400, noise: 0.22, tb: 0.16 }, LOW = { beta: 0.22, lp: 2000, noise: 0.14, tb: 0.13 };
const apDelay = (c, w) => 1 - 2 * Math.atan2(c * Math.sin(w), 1 + c * Math.cos(w)) / w;   // 1st-order allpass phase delay
/** T60 of the fundamental: 1.5·(196/f)^0.55 s (C2 2.6 s · G3 1.5 s · G4 1.0 s): the oud dies away quickly */
const t60 = f => clamp(1.5 * Math.pow(196 / f, 0.55), 0.55, 2.6);

/** one string (the delay line is the output), added into y from `off` samples on, × amp */
function string(rate, f, R, seed, y, off, amp) {
  const P = rate / f, w0 = TWO_PI * f / rate, wb = TWO_PI * FB / rate, T0 = t60(f), N = y.length - off;
  const A0 = Math.pow(10, -3 / (f * T0)), want = Math.pow(10, -3 / (f * Math.min(R.tb, 0.8 * T0))) / A0;
  const mag = (a, w) => (1 - a) / Math.sqrt(1 - 2 * a * Math.cos(w) + a * a);
  let lo = 0, hi = 0.97;
  for (let k = 0; k < 36; k++) { const m = (lo + hi) / 2; if (mag(m, wb) / mag(m, w0) > want) lo = m; else hi = m; }
  const a = (lo + hi) / 2, b0 = (A0 / mag(a, w0)) * (1 - a);
  const rest = P - Math.atan2(a * Math.sin(w0), 1 - a * Math.cos(w0)) / w0, L = Math.max(2, Math.floor(rest - 0.5)), d = rest - L;
  let cl = -0.9, ch = 0.9;
  for (let k = 0; k < 36; k++) { const m = (cl + ch) / 2; if (apDelay(m, w0) > d) cl = m; else ch = m; }
  const C = (cl + ch) / 2;
  // the pluck: a triangle peaking at β·L + noise combed at the same point, rounded by the plectrum (a periodic one-pole LP
  // run twice), mean removed; the loop filters are primed with that lap so the second lap follows without a step
  const pk = Math.max(1, Math.round(R.beta * L)), e = new Float64Array(L), nz = new Float64Array(L), r = rng(seed >>> 0);
  const al = 1 - Math.exp(-TWO_PI * R.lp / rate);
  for (let i = 0; i < L; i++) nz[i] = r() * 2 - 1;
  let ns = 0;
  const nc = Float64Array.from(nz, (v, i) => v - nz[(i - pk + L) % L]);
  for (let i = 0; i < L; i++) ns += nc[i] * nc[i];
  const nk = R.noise * 0.3 / (Math.sqrt(ns / L) || 1);
  for (let i = 0; i < L; i++) e[i] = (1 - R.noise) * (i <= pk ? i / pk : (L - i) / (L - pk)) + nk * nc[i];
  let z = 0, z2 = 0, mean = 0;
  for (let lap = 0; lap < 2; lap++) for (let i = 0; i < L; i++) { z += al * (e[i] - z); z2 += al * (z - z2); if (lap) e[i] = z2; }
  for (let i = 0; i < L; i++) mean += e[i] / L;
  for (let i = 0; i < L; i++) e[i] -= mean;
  let h = 0, tx = 0, ty = 0;
  const loop = x => { h = b0 * x + a * h; const tv = C * h + tx - C * ty; tx = h; ty = tv; return tv; };
  for (let i = 0; i < L; i++) loop(e[i]);
  const s = new Float32Array(N);
  for (let n = 0; n < N; n++) s[n] = n < L ? e[n] : loop(s[n - L]);
  for (let n = 0; n < N; n++) y[n + off] += amp * s[n];
}

function render(rate, midi) {
  const f0 = mtof(midi), u = rng((midi * 7919 + 17) >>> 0), c = 1.5 + 2 * u(), R = midi < 45 ? LOW : PLEC;
  // the buffer lasts 0.6 of the T60 (0.7–1.5 s), then fades
  const N = Math.round(clamp(0.6 * t60(f0), 0.7, 1.5) * rate), y = new Float32Array(N), lag = Math.round((0.0002 + 0.0004 * u()) * rate);
  string(rate, f0 * Math.pow(2, -c / 2400), R, midi * 97 + 3, y, 0, 1);
  string(rate, f0 * Math.pow(2, c / 2400), R, midi * 97 + 5, y, lag, 0.8);
  // the bowl hears the force at the bridge, the strings' slope y[n] − y[n−1] (+6 dB/oct), then a DC blocker (~20 Hz), a
  // 1.8 ms attack ramp, a 0.15 s end fade, and RMS of 0–0.3 s → 0.2
  const hp = 1 - TWO_PI * 20 / rate, na = Math.max(2, Math.round(0.0018 * rate)), nf = Math.round(0.15 * rate), n3 = Math.min(N, Math.round(0.3 * rate));
  let px = 0, py = 0, yl = 0, ss = 0;
  for (let n = 0; n < N; n++) {
    const sl = y[n] - yl; yl = y[n];
    py = sl - px + hp * py; px = sl;
    let v = py;
    if (n < na) v *= 0.5 - 0.5 * Math.cos(Math.PI * n / na);
    if (n >= N - nf) v *= 0.5 + 0.5 * Math.cos(Math.PI * (n - N + nf) / nf);
    y[n] = v;
    if (n < n3) ss += v * v;
  }
  const k = 0.2 / (Math.sqrt(ss / n3) || 1);
  for (let n = 0; n < N; n++) y[n] *= k;
  return y;
}

/** The cached course of one note, rendered on first use (the chords, the runs and the bass share the cache). */
export function pluck(ac, midi) {
  const rate = ac.sampleRate >= 44100 ? Math.round(ac.sampleRate / 2) : ac.sampleRate, key = `${rate}:${midi}`;
  let b = CACHE.get(key);
  if (!b) {
    const t = performance.now(), y = render(rate, midi);
    b = ac.createBuffer(1, y.length, rate);
    b.getChannelData(0).set(y);
    CACHE.set(key, b);
    const ms = performance.now() - t;
    OUD_STATS.built++; OUD_STATS.ms += ms; OUD_STATS.maxMs = Math.max(OUD_STATS.maxMs, ms);
  }
  return b;
}

/* ---------------- the oud (the style's `ep`) ---------------- */
// Levels (tuning knobs): the oud against the EP it replaces, its bass course (below MIDI 48: the bass doubles it), the
// reverb send, the tremolo's strokes against a single pluck.
export const OUD = { level: 10.6, bass: 0.72, send: 0.3, trem: 0.72 };
const COURSES = 6;                                   // C2 F2 A2 D3 G3 C4: a 4th apart from MIDI 36
const courseOf = m => clamp(Math.floor((m - 36) / 5), 0, COURSES - 1);

/**
 * createOud(ac, out, send, plan, opts) → { chord, note, warm, stats, dispose }
 * Every chord event draws its choices from its own seed (seedOf its notes, velocity and length), not from a running stream.
 * plan.ep.lp: the bus low-pass; plan.oud: { scale (the maqam's degrees), trem, slide, flourish }; plan.key.tonic.
 * opts: { registry, tag, cap, r, t0, log, stepDur }.
 * Bus: 6 course panners (−.2 … +.2) → HP 95 Hz (the bass course's fundamental is the bass's) → bowl 125 Hz (+2 dB,
 * Q 1.1) → wood 300 Hz (+2.5 dB, Q 1.2) → soundboard 650 Hz (+1.5 dB) → the risha 2.5 kHz (+1.5 dB, Q 1.2) → allpass 900 Hz
 * → LP plan.ep.lp (Q .6) → out ×10.6, reverb send .3.
 * Per note: A = .16·v^1.3 (×.72 on the bass course, below MIDI 48), ±2 ¢, release τ = the event's rel; a new pluck on a
 * ringing course damps it (τ 12 ms), so no more than 6 courses (plus their 12 ms tails) sound.
 * Articulation: one or two notes held ≥ 5 steps tremble (p plan.oud.trem): the top note re-plucked every 32nd (every 16th in
 * lite), 7.7–13 Hz, down-strokes 1 · up-strokes .72 (×.72 against a pluck), swelling in over the first quarter, at most 14
 * strokes (8 in lite) and the last left to ring. A single note within 1–4
 * semitones of the one before (≤ 0.7 s) slides from it (p plan.oud.slide, τ 40 ms), else scoops from a semitone below (p
 * .3 of that). Held chords (intro, break, outro) roll up and tremble or strike bass + top on beat 3; the first half may open
 * with a run down the maqam onto the top note (p plan.oud.flourish); the final chord rolls slowly and its top note trembles
 * away over a bar, the bass struck once more.
 */
export function createOud(ac, out, send, plan, { registry, tag, cap = 16, r = Math.random, t0 = ac.currentTime, log = null, stepDur = 0.2 }) {
  const P = plan.oud, tonic = plan.key.tonic, scale = P.scale;
  const nodes = [], N = x => (nodes.push(x), x);
  const busIn = N(gain(ac, 1)), pans = Array.from({ length: COURSES }, (_, k) => N(panner(ac, -0.2 + (0.4 * k) / (COURSES - 1))));
  const hp = N(filter(ac, 'highpass', 95, 0.7)), bowl = N(filter(ac, 'peaking', 125, 1.1, 2)), wood = N(filter(ac, 'peaking', 300, 1.2, 2.5));
  const top = N(filter(ac, 'peaking', 650, 1, 1.5)), risha = N(filter(ac, 'peaking', 2500, 1.2, 1.5)), ap = N(filter(ac, 'allpass', 900, 0.7));
  const lp = N(filter(ac, 'lowpass', plan.ep.lp, 0.6)), outG = N(gain(ac, OUD.level)), sendG = N(gain(ac, OUD.send));
  for (const p of pans) p.connect(busIn);
  const chain = [busIn, hp, bowl, wood, top, risha, ap, lp, outG];
  chain.forEach((n, k) => k && chain[k - 1].connect(n));
  outG.connect(out); outG.connect(sendG); sendG.connect(send);
  const bus = registry.add({ src: [], all: nodes, start: t0, end: Infinity, tag: `${tag}:p` });
  const inScale = m => scale.includes(mod12(m - tonic));
  /** the maqam's next note above (dir 1) or below (−1), within a major 3rd (the augmented 2nd is 3 semitones) */
  const neighbour = (m, dir) => { for (let k = m + dir; Math.abs(k - m) <= 4; k += dir) if (inScale(k)) return k; return null; };
  const tremStep = clamp(cap < 16 ? stepDur : stepDur / 2, 0.075, 0.13), base = Math.floor(r() * 4294967296);
  let voices = [], maxActive = 0, steals = 0, notes = 0, pending = [], lastOne = null, R = r;

  const damp = (x, t) => { registry.stop(x.v, t, 0.012); x.end = t; if (x.rec) x.rec[1] = Math.max(x.rec[0], Math.min(x.rec[1], t)); };
  /** make room for a note on course c at t: the course's ringing note is damped (τ 12 ms), over the cap the oldest goes */
  function claim(t, c) {
    voices = voices.filter(x => x.end > t);
    for (const x of voices) if (x.c === c && x.start <= t + 1e-3) damp(x, t);
    voices = voices.filter(x => x.end > t);
    if (voices.length >= cap) { const old = voices.shift(); registry.stop(old.v, t, 0.01); old.end = t; if (old.rec) old.rec[1] = t; steals++; }
  }
  function track(v, c, start, end) {
    const x = { v, c, start, end, rec: null };
    if (log) { x.rec = [start, end]; log.push(x.rec); }
    voices.push(x);
    notes++;
    maxActive = Math.max(maxActive, voices.length);
  }
  /** one pluck on course c; from: a note to slide from (the finger glides into this one after the stroke) */
  function note(t, midi, vel, dur, relTau = 0.08, c = courseOf(midi), from = null) {
    const buf = pluck(ac, midi);
    claim(t, c);
    const pr = Math.pow(2, (R() * 4 - 2) / 1200), s = ac.createBufferSource(), g = gain(ac, 0);
    s.buffer = buf; s.playbackRate.value = pr;
    if (from != null && from !== midi) { s.playbackRate.setValueAtTime(pr * Math.pow(2, (from - midi) / 12), t); s.playbackRate.setTargetAtTime(pr, t + 0.012, 0.04); }
    s.connect(g); g.connect(pans[c]);
    const A = 0.16 * Math.pow(Math.min(1, vel), 1.3) * (midi < 48 ? OUD.bass : 1), off = t + Math.max(0.05, dur);
    g.gain.setValueAtTime(A, t);
    g.gain.setTargetAtTime(0, off, relTau);
    const bufEnd = t + buf.duration / pr + (from != null ? 0.02 : 0), end = Math.min(bufEnd, off + relTau * 7);
    s.start(t); s.stop(end);
    track(registry.add({ src: [s], all: [s, g], start: t, end, tag, out: g }), c, t, Math.min(bufEnd, off + relTau * 4));
  }
  /** courses of a chord: the top note takes its own course, each lower note its own or the one under the note above */
  function courses(midis) {
    const cs = new Array(midis.length);
    let above = COURSES;
    for (let k = midis.length - 1; k >= 0; k--) { cs[k] = Math.max(0, Math.min(courseOf(midis[k]), above - 1)); above = cs[k]; }
    return cs;
  }
  /** the rish: `midi` re-plucked every tremStep (down 1 · up .72), swelling in over the first quarter, or fading out to .2
   *  (fade), at most 14 strokes (8 in lite), the last one left to ring to the note's end. One voice (one registry entry)
   *  holds every stroke: each stroke damps the one before it (τ 12 ms, scheduled with it), and a new note on the course
   *  or a skip stops the strokes still to come. */
  function tremolo(t, midi, vel, dur, relTau, fade = false, c = courseOf(midi)) {
    const buf = pluck(ac, midi), n = clamp(Math.floor((dur - 0.03) / tremStep), 2, cap < 16 ? 8 : 14), off = t + Math.max(0.05, dur);
    claim(t, c);
    const bus = gain(ac, 1), src = [], all = [bus], at = [];
    bus.connect(pans[c]);
    for (let k = 0; k < n; k++) at.push(t + k * tremStep + (k ? (k % 2 ? 0.08 * tremStep : 0) + (R() - 0.5) * 0.006 : 0));
    let end = t;
    for (let k = 0; k < n; k++) {
      const x = k / n, env = fade ? 1 - 0.8 * x : x < 0.25 ? 0.7 + 1.2 * x : 1 - 0.3 * (x - 0.25) / 0.75;
      const pr = Math.pow(2, (R() * 4 - 2) / 1200), s = ac.createBufferSource(), g = gain(ac, 0), tk = at[k], last = k === n - 1;
      s.buffer = buf; s.playbackRate.value = pr;
      s.connect(g); g.connect(bus);
      g.gain.setValueAtTime(0.16 * Math.pow(Math.min(1, vel * OUD.trem * env * (k % 2 ? 0.72 : 1)), 1.3), tk);
      const stop = last ? Math.min(tk + buf.duration / pr, off + relTau * 7) : at[k + 1] + 0.1;
      g.gain.setTargetAtTime(0, last ? Math.max(tk + 0.05, off) : at[k + 1], last ? relTau : 0.012);
      s.start(tk); s.stop(stop);
      src.push(s); all.push(s, g);
      end = Math.max(end, stop);
    }
    src.unshift(src.pop());                          // the last stroke first: the registry frees the voice when it ends
    track(registry.add({ src, all, start: t, end, tag, out: bus }), c, t, Math.min(end, off + relTau * 4));
  }
  /** a single note: slides from the last single note within a major 3rd (p slide), else a scoop from below (p .3·slide) */
  function single(t, m, vel, dur, relTau) {
    let from = null;
    if (m >= 48) {
      const iv = lastOne ? Math.abs(m - lastOne.m) : 0;
      if (iv >= 1 && iv <= 4 && t - lastOne.t < 0.7 && R() < P.slide) from = lastOne.m;
      else if (R() < 0.3 * P.slide) from = m - 1;
      lastOne = { m, t };
    }
    note(t, m, vel, dur, relTau, courseOf(m), from);
  }
  /** a run down the maqam onto the top note (up to 3 notes from above it, 32nds), the bass course ringing under it; the
   *  top note then trembles (p trem) or rings. → the time the top note lands */
  function flourish(t, midis, vel, left, relTau, cs) {
    const top = midis[midis.length - 1], run = [], dt = clamp(stepDur / 2, 0.08, 0.12);
    for (let m = top, k = 0; k < 3; k++) { const u = neighbour(m, 1); if (u == null || u > 69) break; run.unshift(u); m = u; }
    note(t, midis[0], vel, left, relTau, cs[0]);
    run.forEach((m, k) => note(t + (k + 1) * dt, m, vel * (0.62 + 0.06 * k), dt * 1.2, relTau, cs[midis.length - 1]));
    const land = t + (run.length + 1) * dt, rest = left - (land - t);
    if (rest > 4 * stepDur && R() < P.trem) tremolo(land, top, vel * 0.8, Math.min(rest, 8 * stepDur), relTau, false, cs[midis.length - 1]);
    else note(land, top, vel * 0.85, rest, relTau, cs[midis.length - 1], run.length && R() < P.slide ? run[run.length - 1] : null);
  }
  /** held chords (intro, break, outro: rel ≥ .1) */
  function held(t, midis, vel, dur, relTau) {
    const cs = courses(midis), n = midis.length, half = 8 * stepDur, gap = clamp(0.4 * stepDur, 0.04, 0.075);
    if (relTau >= 0.5) {                               // the final chord
      midis.forEach((m, k) => note(t + k * 0.11, m, vel * (0.9 + 0.04 * k), dur, relTau, cs[k]));
      if (dur > 12 * stepDur) {
        tremolo(t + 4 * stepDur, midis[n - 1], vel * 0.62, Math.min(dur - 4 * stepDur, 14 * stepDur), 0.2, true, cs[n - 1]);
        if (dur > 18 * stepDur) note(t + 16 * stepDur, midis[0], vel * 0.5, dur - 16 * stepDur, relTau, cs[0]);
      }
      return;
    }
    const halves = Math.max(1, Math.min(4, Math.floor((dur + 0.05) / half)));
    for (let j = 0; j < halves; j++) {
      const tj = t + j * half, left = dur - j * half;
      if (j % 2 === 0) {
        if (j === 0 && n >= 3 && R() < P.flourish) flourish(tj, midis, vel * 0.95, left, relTau, cs);
        else midis.forEach((m, k) => note(tj + k * gap * (1 + (R() - 0.5) * 0.2), m, vel * (j ? 0.9 : 1) * (0.88 + 0.04 * k), left - k * gap, relTau, cs[k]));
      } else if (R() < P.trem) {                     // beat 3: the bass, and the top note trembling to the half's end
        note(tj, midis[0], vel * 0.72, left, relTau, cs[0]);
        tremolo(tj + 0.012, midis[n - 1], vel * 0.66, Math.min(left, half) - 0.02, relTau, false, cs[n - 1]);
      } else {                                       // beat 3: bass + top, the next note down an 8th later
        note(tj, midis[0], vel * 0.72, left, relTau, cs[0]);
        note(tj + 0.012, midis[n - 1], vel * 0.66, left - 0.012, relTau, cs[n - 1]);
        if (n > 2 && 2 * stepDur < left) note(tj + 2 * stepDur, midis[n - 2], vel * 0.56, left - 2 * stepDur, relTau, cs[n - 2]);
      }
    }
  }
  return {
    note,
    /** one plectrum stroke across the courses, bottom to top over `strum` s (the top +5 %, ±5 % per note); one or two
     *  long notes may tremble; single notes may slide */
    chord(t, midis, vel, dur, strum = 0.02, relTau = 0.08) {
      const n = midis.length, top = midis[n - 1];
      R = rng(seedOf(base, vel, dur, relTau, ...midis));
      if (relTau >= 0.095 && n >= 3 && dur >= 7.5 * stepDur) { held(t, midis, vel, dur, relTau); lastOne = null; }
      else if (n <= 2 && top >= 48 && dur >= 5 * stepDur && R() < P.trem) {
        const cs = courses(midis);
        if (n === 2) note(t, midis[0], vel, dur, relTau, cs[0]);
        tremolo(t + (n === 2 ? 0.01 : 0), top, vel, dur, relTau, false, cs[n - 1]);
        lastOne = { m: top, t };
      } else if (n === 1) single(t, top, vel, dur, relTau);
      else {
        const cs = courses(midis);
        midis.forEach((m, k) => {
          const dt = (k * strum) / (n - 1), v = vel * (k === n - 1 ? 1.05 : 1) * (1 + (R() * 2 - 1) * 0.05);
          note(t + dt, m, v, dur - dt, relTau, cs[k]);
        });
        lastOne = top >= 48 ? { m: top, t } : null;
      }
      if (pending.length) pluck(ac, pending.shift());
    },
    /** render the plucks of these notes (the track's chords and bass roots): now within `ms` of JS, the rest one per chord */
    warm(midis, ms = 3) {
      const t = performance.now();
      for (const m of new Set(midis)) { if (performance.now() - t < ms) pluck(ac, m); else pending.push(m); }
    },
    stats: () => ({ maxActive, steals, notes, active: voices.length }),
    dispose(t) { bus.end = t; },
  };
}

/* ---------------- bass: the oud's low courses over a soft sub ---------------- */
// Levels (tuning knobs): the pluck and the sub against the jazzhop bass.
export const OUD_BASS = { pluck: 0.6, sub: 0.255 };
/**
 * createOudBass(ac, out, p, opts) → { note, dispose }   p: { lp } (the pluck's low-pass, 800–1200 Hz)
 * Pluck (the cached course, MIDI 33–47) → gain .5·v^1.2 → LP p.lp (Q .7) → bowl 100 Hz (+2 dB) → out; a new note damps the
 * last (τ 15 ms), release τ 60 ms. Under it a sub: sine f + triangle f (.12) → LP 260 Hz → amp: τ 12 ms to .2v, settling to
 * .6 of it (τ .5 s), release τ 90 ms. A slide glides both up from a semitone below (τ 30 ms).
 */
export function createOudBass(ac, out, p, { registry, tag, t0 = ac.currentTime }) {
  const nodes = [], N = x => (nodes.push(x), x);
  const inG = N(gain(ac, 1)), lp = N(filter(ac, 'lowpass', p.lp || 1000, 0.7)), body = N(filter(ac, 'peaking', 100, 1, 2));
  inG.connect(lp); lp.connect(body); body.connect(out);
  const sine = N(osc(ac, 'sine', 55)), tri = N(osc(ac, 'triangle', 55)), triG = N(gain(ac, 0.12)), sLP = N(filter(ac, 'lowpass', 260, 0.7)), amp = N(gain(ac, 0));
  sine.connect(sLP); tri.connect(triG); triG.connect(sLP); sLP.connect(amp); amp.connect(out);
  const sub = persistent(registry, tag, [sine, tri], nodes, t0);
  let last = null;
  return {
    note(t, midi, vel, dur, slide = false) {
      const f = mtof(midi), d = Math.max(0.12, dur), buf = pluck(ac, midi), low = Math.pow(2, -1 / 12);
      if (last && last.end > t) { registry.stop(last.v, t, 0.015); last.end = t; }
      const s = ac.createBufferSource(), g = gain(ac, 0);
      s.buffer = buf;
      if (slide) { s.playbackRate.setValueAtTime(low, t); s.playbackRate.setTargetAtTime(1, t + 0.01, 0.03); }
      s.connect(g); g.connect(inG);
      const A = OUD_BASS.pluck * Math.pow(Math.min(1, vel), 1.2), off = t + d, end = Math.min(t + buf.duration + (slide ? 0.02 : 0), off + 0.06 * 7);
      g.gain.setValueAtTime(A, t); g.gain.setTargetAtTime(0, off, 0.06);
      s.start(t); s.stop(end);
      last = { v: registry.add({ src: [s], all: [s, g], start: t, end, tag, out: g }), end };
      for (const prm of [sine.frequency, tri.frequency, amp.gain]) hold(prm, t);
      for (const o of [sine, tri]) {
        if (slide) { o.frequency.setValueAtTime(f * low, t); o.frequency.setTargetAtTime(f, t + 0.01, 0.03); }
        else o.frequency.setValueAtTime(f, t);
      }
      const S = OUD_BASS.sub * vel;
      amp.gain.setTargetAtTime(S, t, 0.012);
      amp.gain.setTargetAtTime(0.6 * S, t + 0.05, 0.5);
      amp.gain.setTargetAtTime(0, off, 0.09);
    },
    dispose(t) { sub.dispose(t); },
  };
}
